/**
 * How a library section is ordered, and how that choice is remembered.
 *
 * WHY "RELEASED" EXISTS, since it is the reason this file does.
 *
 * A wrestling library cannot be sorted by title. The events do not agree on a
 * numbering system — WrestleMania IV, WrestleMania 2000 and WrestleMania 26
 * are the same series named three different ways — so alphabetically they
 * land nowhere near each other, and "2000" sorts next to "21" rather than
 * next to the event that followed it. The air date is the one thing every
 * event has and agrees on, so ordering by it produces the order somebody
 * means when they say "in order".
 *
 * That is worth having everywhere, not just for wrestling: it is also how you
 * watch a film series, and how you find the oldest thing in a library.
 */
export const SORTS = [
  { id: 'title', name: 'Title', desc: false },
  { id: 'released', name: 'Release date', desc: false },
  { id: 'added', name: 'Recently added', desc: true },
  { id: 'rating', name: 'Rating', desc: true },
] as const;

export type LibrarySort = (typeof SORTS)[number]['id'];

/** The direction that makes each one useful without being asked. */
export function defaultDesc(sort: LibrarySort): boolean {
  return SORTS.find((s) => s.id === sort)?.desc ?? false;
}

export function sortName(sort: LibrarySort): string {
  return SORTS.find((s) => s.id === sort)?.name ?? 'Title';
}

export function isSort(v: unknown): v is LibrarySort {
  return typeof v === 'string' && SORTS.some((s) => s.id === v);
}

/*
 * PER SECTION, and remembered.
 *
 * One setting for the whole library would be wrong in both directions: the
 * order that makes Wrestling usable is not the order anybody wants for
 * Movies, and having to set it again on every visit would make it not worth
 * setting. Kept in localStorage because it is a per-viewer convenience on one
 * device — losing it costs a single button press, so it is not worth a column
 * in the database or a round trip on every page load.
 */
const KEY = 'n8m.libsort';

type Stored = Record<string, { sort: LibrarySort; desc: boolean }>;

function read(): Stored {
  try { return JSON.parse(localStorage.getItem(KEY) ?? '{}') as Stored; } catch { return {}; }
}

/**
 * What this viewer sees when they open a section.
 *
 * THREE LAYERS, most personal first: what this viewer chose on this device,
 * then what an admin set for everyone, then title.
 *
 * The middle layer is why this takes an argument. Wrestling is unusable in
 * title order for EVERY viewer, not just the one who noticed — so an admin
 * sets it once for the household rather than five people each discovering the
 * sort dialog. It stays a starting point: anybody who picks their own order
 * keeps it, and the admin default never overrides a choice already made.
 */
export function loadSort(
  section: string,
  fallback?: { sort: string | null; desc: boolean },
): { sort: LibrarySort; desc: boolean } {
  const saved = read()[section];
  if (saved && isSort(saved.sort)) return { sort: saved.sort, desc: !!saved.desc };
  if (fallback && isSort(fallback.sort)) return { sort: fallback.sort, desc: !!fallback.desc };
  return { sort: 'title', desc: false };
}

export function saveSort(section: string, sort: LibrarySort, desc: boolean): void {
  try {
    localStorage.setItem(KEY, JSON.stringify({ ...read(), [section]: { sort, desc } }));
  } catch {
    // Private windows and cleared site data both throw here. The sort still
    // works for this visit; only remembering it is lost, which is not worth
    // failing a render over.
  }
}
